import { debounce } from "@mui/material";

import { Building2Icon, MapPinIcon, ShieldUserIcon, SquarePenIcon, Users2Icon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { Organization } from "verdi-common/apiTypes/organization";
import { formatTime } from "verdi-common/utils/index";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { UserProfile } from "@/api/orval/generated/schemas";
import { Icon } from "@/components/icon";
import SimpleBarCompatibility from "@/components/simple-bar-compatibility/simple-bar-compatibility";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogPopup } from "@/components/ui/dialog";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { SIZE } from "@/configs/sizes";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS } from "@/icons";
import {
    SortableTableHeader,
    TableFramed,
    type TableHeadingData,
    type TableRowData,
} from "./specialized-components/account-manager-table";
import { OrganizationDetailsPanel } from "./specialized-components/organization-details-panel";
import { UserDetailsPanel } from "./specialized-components/user-details-panel";

function MapLink({
    coordinates,
    defaultValue,
}: {
    coordinates?: { latitude?: number; longitude?: number };
    defaultValue?: React.ReactNode;
}) {
    const latitude = coordinates?.latitude;
    const longitude = coordinates?.longitude;

    const invalidCoordinates =
        typeof latitude !== "number" ||
        typeof longitude !== "number" ||
        Number.isNaN(latitude) ||
        Number.isNaN(longitude);

    if (invalidCoordinates) {
        return defaultValue;
    }

    return (
        <Badge className={"rounded-lg p-2"} variant={"info"}>
            <a
                className={"flex flex-row gap-1 hover:no-underline!"}
                rel={"noreferrer"}
                target={"_blank"}
                href={`https://www.openstreetmap.org/search?lat=${latitude}&lon=${longitude}&zoom=17`}
            >
                <MapPinIcon className={"size-6"} />
                {`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`}
            </a>
        </Badge>
    );
}

const USERS_QUERY_LIMIT = 500;
const ORGANIZATIONS_QUERY_LIMIT = 500;

const ACCOUNT_MANAGER_TAB = {
    USERS: "tab-users",
    ORGANIZATIONS: "tab-organizations",
} as const;

type AccountManagerTab = (typeof ACCOUNT_MANAGER_TAB)[keyof typeof ACCOUNT_MANAGER_TAB];

export default function AccountManager() {
    const { t } = useTranslation(NAMESPACES.COMMON);
    const [detailsDialog, setDetailsDialog] = React.useState<{
        open: boolean;
        content?: React.ReactNode;
    }>({
        open: false,
        content: null,
    });
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState<AccountManagerTab>(ACCOUNT_MANAGER_TAB.USERS);

    const showUserDetailsDialog = (user?: UserProfile) => {
        setDetailsDialog({
            open: true,
            content: (
                <UserDetailsPanel
                    user={user}
                    onClose={() => {
                        setDetailsDialog({ open: false, content: null });
                    }}
                    onSave={(updatedUser) => {
                        if (updatedUser) {
                            setUsers((prevUsers) => {
                                const updatedUserIndex = prevUsers.findIndex((u) => u.userId === updatedUser.userId);
                                if (updatedUserIndex !== -1) {
                                    // Create a new array with the updated user
                                    return prevUsers.map((existingUser) =>
                                        updatedUser.userId === existingUser.userId ? updatedUser : existingUser,
                                    );
                                }
                                return [...prevUsers, updatedUser];
                            });
                        }
                    }}
                />
            ),
        });
    };

    const showOrganizationDetailsDialog = (organization?: Organization) => {
        setDetailsDialog({
            open: true,
            content: (
                <OrganizationDetailsPanel
                    organization={organization}
                    onClose={() => setDetailsDialog({ open: false, content: null })}
                    onSave={(updatedOrganization) => {
                        if (updatedOrganization) {
                            setOrganizations((prevOrganizations) => {
                                const updatedOrganizationIndex = prevOrganizations.findIndex(
                                    (o) => o.id === updatedOrganization.id,
                                );
                                if (updatedOrganizationIndex !== -1) {
                                    // Create a new array with the updated organization
                                    return prevOrganizations.map((existingOrganization) =>
                                        updatedOrganization.id === existingOrganization.id
                                            ? updatedOrganization
                                            : existingOrganization,
                                    );
                                }
                                return [...prevOrganizations, updatedOrganization];
                            });
                        }
                    }}
                />
            ),
        });
    };

    const userStatusBadgeColors: Record<string, "success" | "destructive" | "info"> = {
        enabled: "success",
        disabled: "destructive",
        invited: "info",
    };

    const userHeadings: TableHeadingData[] = [
        { content: t("labels.name"), props: { className: "w-full" } },
        { content: t("labels.login-ids"), props: { className: "hidden lg:table-cell" } },
        { content: t("labels.email"), props: { className: "hidden 2xl:table-cell" } },
        { content: t("labels.phone"), props: { className: "hidden xl:table-cell" } },
        {
            content: <SortableTableHeader>{t("labels.created")}</SortableTableHeader>,
            props: { className: "hidden sm:table-cell" },
        },
        { content: t("labels.status") },
    ];

    const userRows: TableRowData[] = users.map((user) => ({
        cells: [
            {
                content: (
                    <>
                        <span className={"flex flex-row items-center gap-1 font-semibold text-sm sm:text-base"}>
                            {user.givenName && user.familyName ? `${user.givenName} ${user.familyName}` : user.name}
                            {(user.roleNames ?? []).find((roleName) => roleName === "Verdi Administrator") && (
                                <Badge variant={"ghost"} title={"Administrator"}>
                                    <ShieldUserIcon className={"size-6 stroke-primary"} />
                                </Badge>
                            )}
                        </span>
                        <pre className={"text-xs sm:text-inherit text-black/50"}>{user.userId}</pre>
                    </>
                ),
            },
            {
                props: { className: "hidden lg:table-cell" },
                content: (
                    <ul className={"flex flex-row gap-2"}>
                        {user.loginIds.slice(0, 2).map((loginId, loginIdIndex) => (
                            <li key={loginId}>
                                <Badge className={"rounded-lg p-2"} variant={loginIdIndex === 0 ? "info" : "outline"}>
                                    {loginId}
                                </Badge>
                            </li>
                        ))}
                        {user.loginIds.length > 2 && (
                            <li>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Badge className={"rounded-lg p-2"} variant={"outline"}>
                                            +{user.loginIds.length - 2} more
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipPopup>
                                        <div className={"flex flex-row gap-2 p-2"}>
                                            {user.loginIds.slice(2).map((loginId) => (
                                                <Badge className={"rounded-lg p-2"} variant={"outline"} key={loginId}>
                                                    {loginId}
                                                </Badge>
                                            ))}
                                        </div>
                                    </TooltipPopup>
                                </Tooltip>
                            </li>
                        )}
                    </ul>
                ),
            },
            {
                props: { className: "hidden 2xl:table-cell" },
                content: <a href={`mailto:${user.email}`}>{user.email}</a>,
            },
            {
                props: { className: "hidden xl:table-cell" },
                content: <a href={`tel:${user.phone}`}>{user.phone}</a>,
            },
            {
                props: { className: "hidden sm:table-cell" },
                content: formatTime(user.createdTime),
            },
            {
                content: (
                    <Badge
                        className={"rounded-lg p-2 text-xs sm:text-base"}
                        variant={user.status in userStatusBadgeColors ? userStatusBadgeColors[user.status] : "outline"}
                    >
                        {user.status}
                    </Badge>
                ),
            },
            {
                content: (
                    <Button size={"icon-xl"} variant={"outline"} onClick={() => showUserDetailsDialog(user)}>
                        <SquarePenIcon className={"size-4 sm:size-6"} />
                    </Button>
                ),
            },
        ],
    }));

    const organizationHeadings: TableHeadingData[] = [
        { content: t("labels.name"), props: { className: "w-full" } },
        { content: t("labels.location"), props: { className: "hidden md:table-cell" } },
        {
            content: <SortableTableHeader>{t("labels.created")}</SortableTableHeader>,
            props: { className: "hidden sm:table-cell" },
        },
        { content: t("labels.status") },
    ];

    const organizationRows: TableRowData[] = organizations.map((organization) => ({
        cells: [
            {
                content: (
                    <>
                        <span className={"flex flex-row items-center gap-1 font-semibold text-sm sm:text-base"}>
                            {organization.name}{" "}
                            {organization.adminUseOnly && (
                                <Badge variant={"ghost"} title={"Admin Use Only"}>
                                    <ShieldUserIcon className={"size-6 stroke-primary"} />
                                </Badge>
                            )}
                        </span>
                        <pre className={"text-xs sm:text-base text-black/50"}>{organization.id}</pre>
                    </>
                ),
            },
            {
                props: { className: "hidden md:table-cell" },
                content: <MapLink coordinates={organization.defaultCoordinates} defaultValue={""} />,
            },
            {
                props: { className: "hidden sm:table-cell" },
                content: organization.createdAt ? formatTime(organization.createdAt) : "",
            },
            {
                content: (
                    <Badge
                        className={"rounded-lg p-2 text-xs sm:text-base"}
                        variant={organization.deleted ? "warning" : "success"}
                    >
                        {organization.deleted ? "deleted" : "active"}
                    </Badge>
                ),
            },
            {
                content: (
                    <Button
                        size={"icon-xl"}
                        variant={"outline"}
                        onClick={() => showOrganizationDetailsDialog(organization)}
                    >
                        <SquarePenIcon className={"size-4 sm:size-6"} />
                    </Button>
                ),
            },
        ],
    }));

    const fetchOrganizations = React.useCallback(async (searchString?: string) => {
        setOrganizationsLoading(true);

        const response = await SessionHandler.doFetch("organizations/search", {
            headers: { "Content-Type": "application/json" },
            method: "POST",
            body: JSON.stringify({
                limit: ORGANIZATIONS_QUERY_LIMIT,
                filter: { name: searchString, id: searchString },
            }),
        });
        if (response.ok) {
            const data = await response.json();
            setOrganizations(data.organizations);
        }

        setOrganizationsLoading(false);
    }, []);

    const fetchUsers = React.useCallback(async (searchString?: string) => {
        setUsersLoading(true);

        const response = await SessionHandler.doFetch("users/search", {
            headers: { "Content-Type": "application/json" },
            method: "POST",
            body: JSON.stringify({
                limit: USERS_QUERY_LIMIT,
                text: searchString,
                sort: [{ field: "createdTime", desc: true }],
            }),
        });
        if (response.ok) {
            const data = await response.json();
            setUsers(data);
        }

        setUsersLoading(false);
    }, []);

    const debouncedSearchOrganizations = useMemo(() => debounce(fetchOrganizations, 300), [fetchOrganizations]);
    const debouncedSearchUsers = useMemo(() => debounce(fetchUsers, 300), [fetchUsers]);

    // Clear any in-flight searches if we leave this page
    useEffect(
        () => () => {
            debouncedSearchOrganizations.clear();
            debouncedSearchUsers.clear();
        },
        [debouncedSearchOrganizations, debouncedSearchUsers],
    );

    // Fetch users on mount since it's the first tab visible
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const refresh = React.useCallback(async () => {
        if (activeTab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS) {
            debouncedSearchOrganizations.clear();
            await fetchOrganizations(searchTerm);
        } else {
            debouncedSearchUsers.clear();
            await fetchUsers(searchTerm);
        }
    }, [activeTab, searchTerm, debouncedSearchOrganizations, debouncedSearchUsers, fetchOrganizations, fetchUsers]);

    const searchPlaceholder =
        activeTab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS ? t("actions.search-organizations") : t("actions.search-users");

    const trimmedSearchTerm = searchTerm.trim();

    let usersEmptyMessage: string | undefined;
    if (!usersLoading && userRows.length === 0) {
        usersEmptyMessage = trimmedSearchTerm
            ? t("messages.no-results-for-query", { query: trimmedSearchTerm })
            : t("messages.no-results");
    }

    let organizationsEmptyMessage: string | undefined;
    if (!organizationsLoading && organizationRows.length === 0) {
        organizationsEmptyMessage = trimmedSearchTerm
            ? t("messages.no-results-for-query", { query: trimmedSearchTerm })
            : t("messages.no-results");
    }

    const handleSearchInputChange = (term: string) => {
        setSearchTerm(term);
        if (activeTab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS) {
            debouncedSearchOrganizations(term);
        } else {
            debouncedSearchUsers(term);
        }
    };

    const handleTabsChange = (tab: AccountManagerTab) => {
        debouncedSearchOrganizations.clear();
        debouncedSearchUsers.clear();

        setActiveTab(tab);
        if (tab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS) {
            fetchOrganizations(searchTerm);
        } else {
            fetchUsers(searchTerm);
        }
    };

    return (
        <SimpleBarCompatibility className={"max-h-[calc(100%-var(--nav-bar-height))]"}>
            <main className={"p-4 sm:p-8"}>
                <h2 className={"text-4xl font-bold mb-4"}>{t("labels.account-manager")}</h2>
                <p className={"mb-4 text-base"}>
                    <Trans
                        i18nKey={"labels.account-manager-description"}
                        t={t}
                        components={{ icon: <SquarePenIcon className={"size-6 inline-block mb-2"} /> }}
                    />
                </p>

                <section>
                    <Tabs
                        className={"gap-4"}
                        onValueChange={(value) => value && handleTabsChange(value)}
                        value={activeTab}
                    >
                        <div className={"flex flex-col-reverse sm:flex-row justify-between gap-2"}>
                            <TabsList className={"flex flex-row justify-center"} variant={"underline"}>
                                <TabsTab value={ACCOUNT_MANAGER_TAB.USERS} className={"sm:text-lg"}>
                                    <Users2Icon className={"size-6"} />
                                    {t("labels.users")}
                                </TabsTab>
                                <TabsTab value={ACCOUNT_MANAGER_TAB.ORGANIZATIONS} className={"sm:text-lg"}>
                                    <Building2Icon className={"size-6"} />
                                    {t("labels.organizations")}
                                </TabsTab>
                            </TabsList>
                            <div className={"w-full md:w-1/2 justify-end flex flex-col sm:flex-row gap-2"}>
                                <SearchInput
                                    clearable
                                    // This page runs a size up; the group's own scale would shrink the field.
                                    className={"sm:text-base!"}
                                    value={searchTerm}
                                    onValueChange={handleSearchInputChange}
                                    placeholder={searchPlaceholder}
                                />
                                <div className={"flex justify-end flex-row gap-2 items-center"}>
                                    <Button
                                        onClick={refresh}
                                        aria-label={
                                            activeTab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS
                                                ? t("actions.refresh-organizations")
                                                : t("actions.refresh-users")
                                        }
                                        tooltip={
                                            activeTab === ACCOUNT_MANAGER_TAB.ORGANIZATIONS
                                                ? t("actions.refresh-organizations")
                                                : t("actions.refresh-users")
                                        }
                                        size={"icon-xxl"}
                                    >
                                        <Icon icon={ICON_KEYS.REFRESH} size={SIZE.SM} ariaHidden />
                                    </Button>
                                    <Button
                                        onClick={() => showUserDetailsDialog()}
                                        aria-label={t("actions.add-user")}
                                        tooltip={t("actions.add-user")}
                                        size={"icon-xxl"}
                                    >
                                        <Icon icon={ICON_KEYS.ADD_USER} size={SIZE.SM} ariaHidden />
                                    </Button>
                                    <Button
                                        onClick={() => showOrganizationDetailsDialog()}
                                        aria-label={t("actions.add-organization")}
                                        tooltip={t("actions.add-organization")}
                                        size={"icon-xxl"}
                                    >
                                        <Icon icon={ICON_KEYS.ADD_ORGANIZATION} size={SIZE.SM} ariaHidden />
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <TabsPanel value={ACCOUNT_MANAGER_TAB.USERS}>
                            <TableFramed
                                loading={usersLoading}
                                headings={userHeadings}
                                rows={userRows}
                                emptyMessage={usersEmptyMessage}
                            />
                        </TabsPanel>
                        <TabsPanel value={ACCOUNT_MANAGER_TAB.ORGANIZATIONS}>
                            <TableFramed
                                loading={organizationsLoading}
                                headings={organizationHeadings}
                                rows={organizationRows}
                                emptyMessage={organizationsEmptyMessage}
                            />
                        </TabsPanel>
                    </Tabs>
                </section>
            </main>
            <Dialog open={detailsDialog.open} onOpenChange={(open) => setDetailsDialog({ ...detailsDialog, open })}>
                <DialogPopup showCloseButton={false}>{detailsDialog.content}</DialogPopup>
            </Dialog>
        </SimpleBarCompatibility>
    );
}
